import { ODSChartsCSSThemesNames, ODSChartsItemCSSDefinition } from '../css-themes/css-themes';
import { isVarArray, isVarObject } from '../../tools/merge-objects';
import { escapeHtml } from '../../tools/escape-html';
const DEFAULT_CSS = `.ods-charts-no-css-lib.ods-charts-legend-holder {
  padding-top: 10px;
  padding-bottom: 20px;
  position: relative;
} 

.ods-charts-no-css-lib .ods-charts-legend-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.ods-charts-no-css-lib .ods-charts-legend-container-vertical {
  flex-direction: column;
}


.ods-charts-no-css-lib .ods-charts-legend-container-horizontal {
  margin-left: 30px;
  margin-right: 30px;
}

.ods-charts-no-css-lib .ods-charts-legend-item {  
  padding-bottom: 0.625rem;
  margin-right: 10px;
  margin-left: 10px;
  color: var(--bs-body-color, #000000);
}
.ods-charts-no-css-lib .ods-charts-legend-custom-content {
  margin-left: 5px;
}
.ods-charts-no-css-lib .ods-charts-legend-global-custom-content {
  margin-left: 10px;
}
.ods-charts-no-css-lib .ods-charts-legend-link {
  text-decoration: none;
}
.ods-charts-no-css-lib .ods-charts-legend-link.ods-charts-legend-link-opacity {
  opacity: .25;
}

.ods-charts-no-css-lib .ods-charts-legend-color-holder {
  display: inline-block;
}

.ods-charts-no-css-lib .ods-charts-legend-color {
  margin-right: 5px;
  width: 12px;
  height: 12px;
  position: relative;
  display: block;
}
.ods-charts-no-css-lib .ods-charts-legend-label {
  cursor: pointer;
  display: inline-block;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
}

[data-bs-theme="dark"] {
  --ods-charts-legend-bg: var(--bs-gray-950, #141414);
  --ods-charts-legend-color: var(--bs-white, #fff);
}
[data-bs-theme="light"] {
  --ods-charts-legend-bg: var(--bs-white, #fff);
  --ods-charts-legend-color: var(--bs-black, #000);
}

.ods-charts-no-css-lib.ods-charts-legend-holder {
  background-color: var(--ods-charts-legend-bg, var(--bs-body-bg, #ffffff));
}
.ods-charts-no-css-lib.ods-charts-legend-holder .ods-charts-legend-item {
  color: var(--ods-charts-legend-color, var(--bs-body-color, #000000));
}
`;
export class ODSChartsLegendData {
    constructor() {
        this.labels = [];
        this.names = [];
    }
}
export class ODSChartsLegends {
    constructor(echart, legendHolders) {
        this.echart = echart;
        this.legendHolders = legendHolders;
    }
    static addLegend(echart, legendHolders) {
        const legendHoldersArray = isVarArray(legendHolders)
            ? legendHolders
            : isVarObject(legendHolders)
                ? [legendHolders]
                : [{ legendHolderSelector: legendHolders }];
        return new ODSChartsLegends(echart, legendHoldersArray);
    }
    /**
     * Extract legend data from the chart options.
     * NB : According to https://echarts.apache.org/en/option.html#legend.data the legend data can be :
     *  - an array of string
     *  - an array of objects containing a name property
     *  - not defined (in this case the legend data is extracted from the series names or from the dataset source first row)
     * The label is the name of the legend displayed on the chart.
     * The name is the name of the serie associated to the legend.
     * That means that the label is equal to the name of the serie, excepted for graph like pie chart where the legend label is the name of the data item.
     *
     * We keep an ODS Charts specific behavior where the legend.data contains labels in place of names
     * and then the array order is used to map the labels to the series names.
     *
     * @param dataOptions : chart data options
     * @param updateDataLegendOption : whether to update the legend option
     * @param updateDataSeriesOption : whether to update the series option
     * @returns {ODSChartsLegendData} : Two arrays labels and names of ordered legends to be displayed and containing respectively the label and the name of the serie.
     * @throws Error if the legend data or series data are missing or inconsistent
     */
    static getLegendData(dataOptions, updateDataLegendOption = true, updateDataSeriesOption = true) {
        let legendData = dataOptions.legend && dataOptions.legend.data
            ? dataOptions.legend.data.map((legendDataItem) => (isVarObject(legendDataItem) ? legendDataItem.name : legendDataItem))
            : [];
        let seriesNames;
        const monoSerieGraphe = 1 === dataOptions.series.length && dataOptions.series[0].data && ['pie'].includes(dataOptions.series[0].type);
        if (updateDataLegendOption) {
            if (dataOptions.legend) {
                dataOptions.legend = Object.assign({}, dataOptions.legend);
            }
        }
        if (updateDataSeriesOption) {
            if (dataOptions.series) {
                dataOptions.series = [...dataOptions.series.map((v) => (Object.assign({}, v)))];
            }
        }
        if (!dataOptions.legend || !dataOptions.legend.data) {
            if (dataOptions.dataset && dataOptions.dataset.source) {
                try {
                    if (updateDataLegendOption && !dataOptions.legend) {
                        dataOptions.legend = {};
                    }
                    legendData = dataOptions.dataset.source[0].reduce((accumulator, currentValue, currentIndex) => {
                        if (currentIndex > 0) {
                            accumulator.push(currentValue);
                        }
                        return accumulator;
                    }, []);
                    if (updateDataLegendOption) {
                        dataOptions.legend.data = legendData;
                    }
                }
                catch (error) {
                    throw new Error(`Missing data array of legends in legend chart option`, { cause: error });
                }
            }
            else if (!dataOptions.series) {
                throw new Error(`Missing data array of legends in legend chart option`);
            }
            else {
                if (updateDataLegendOption && !dataOptions.legend) {
                    dataOptions.legend = {};
                }
                if (monoSerieGraphe) {
                    legendData = dataOptions.series[0].data.map((serie, _index) => {
                        if (!serie.name) {
                            throw new Error(`Missing data array of legends in legend chart option`);
                        }
                        return serie.name;
                    });
                }
                else {
                    legendData = dataOptions.series.map((serie, _index) => {
                        if (!serie.name) {
                            throw new Error(`Missing data array of legends in legend chart option`);
                        }
                        return serie.name;
                    });
                }
                if (updateDataLegendOption) {
                    dataOptions.legend.data = legendData;
                }
            }
        }
        if (monoSerieGraphe) {
            seriesNames = dataOptions.series[0].data.map((serie, _index) => {
                if (!serie.name) {
                    throw new Error(`Missing data array of legends in legend chart option`);
                }
                return serie.name;
            });
        }
        else {
            if (updateDataSeriesOption && !dataOptions.series) {
                dataOptions.series = legendData.map((_serie, index) => ({
                    name: legendData[index] || 'serie_Name_' + index,
                }));
            }
            if (!dataOptions.series) {
                throw new Error(`Missing series array in chart option`);
            }
            const unusedseriesNames = legendData.filter((legendLabel) => {
                return !dataOptions.series.find((serie) => serie.name === legendLabel);
            });
            seriesNames = dataOptions.series.map((serie, index) => {
                if (!serie.name) {
                    if (!updateDataSeriesOption) {
                        throw new Error(`Missing series names in chart option`);
                    }
                    serie.name = unusedseriesNames.shift() || 'serie_Name_' + index;
                }
                return serie.name;
            });
        }
        let displayedSeriesNames = legendData.filter((legendLabel) => seriesNames.includes(legendLabel));
        let displayedSeriesLabels = displayedSeriesNames;
        if (legendData.length !== displayedSeriesNames.length) {
            // eslint-disable-next-line no-console
            console.info(`The legend data array contains some legends that do not match any series name. Legend data: [${legendData}]. Series names: [${seriesNames}]. Displayed legends: [${displayedSeriesNames}]`);
            // displayedSeriesNames differs from legendData,
            // that means that some legends do not match any serie name
            // We asumed then that legend.data is an order list of labels
            // that will match the series names by their index
            displayedSeriesLabels = legendData;
            if (displayedSeriesLabels.length > seriesNames.length) {
                displayedSeriesLabels = displayedSeriesLabels.filter((_legendLabel, index) => index < seriesNames.length);
            }
            displayedSeriesNames = seriesNames.filter((_seriesName, index) => index < displayedSeriesLabels.length);
            // eslint-disable-next-line no-console
            console.info(`Displayed legends labels have been mapped by their index, [${displayedSeriesLabels}] are the labels of the displayed series [${displayedSeriesNames}]`);
        }
        return {
            names: displayedSeriesNames,
            labels: displayedSeriesLabels,
        };
    }
    addLegend(dataOptions, colors, cssTheme, cssThemeName, mode) {
        if (ODSChartsCSSThemesNames.NONE === cssThemeName && !document.querySelector('#ods-chart-legends-none-css')) {
            let style = document.createElement('style');
            style.id = 'ods-chart-legends-none-css';
            style.textContent = DEFAULT_CSS;
            document.head.appendChild(style);
        }
        // keep Apache ECharts default configuration for legend orientation
        if (dataOptions && dataOptions.legend && dataOptions.legend.orient) {
            for (const legendHolder of this.legendHolders) {
                if (!legendHolder.orientation) {
                    legendHolder.orientation = dataOptions.legend.orient;
                }
            }
        }
        const allLegends = ODSChartsLegends.getLegendData(dataOptions);
        const legendHolders = {};
        let defaultLegendHolder = undefined;
        for (const legendHolder of this.legendHolders) {
            if (!document.querySelector(legendHolder.legendHolderSelector)) {
                throw new Error(`No legend holder found with selector ${legendHolder.legendHolderSelector}`);
            }
            legendHolders[legendHolder.legendHolderSelector] = Object.assign(Object.assign({}, legendHolder), { legends: { labels: [], names: [], index: [] } });
            if (!legendHolder.seriesRef) {
                defaultLegendHolder = legendHolders[legendHolder.legendHolderSelector];
            }
        }
        for (let index = 0; index < allLegends.names.length && index < allLegends.labels.length; index++) {
            let legendHolderSelector = Object.keys(legendHolders).find((legendHolderKey) => {
                var _a, _b;
                return !!legendHolders[legendHolderKey].seriesRef &&
                    (((_a = legendHolders[legendHolderKey].seriesRef) === null || _a === void 0 ? void 0 : _a.includes(allLegends.names[index])) ||
                        ((_b = legendHolders[legendHolderKey].seriesRef) === null || _b === void 0 ? void 0 : _b.includes(allLegends.labels[index])));
            });
            if (!legendHolderSelector) {
                const series = dataOptions.series && dataOptions.series.find((series) => series.name === allLegends.names[index]);
                if (series && series.stack) {
                    legendHolderSelector = Object.keys(legendHolders).find((legendHolderKey) => { var _a; return !!legendHolders[legendHolderKey].seriesRef && ((_a = legendHolders[legendHolderKey].seriesRef) === null || _a === void 0 ? void 0 : _a.includes(series.stack)); });
                }
            }
            if (legendHolderSelector) {
                legendHolders[legendHolderSelector].legends.labels.push(allLegends.labels[index]);
                legendHolders[legendHolderSelector].legends.names.push(allLegends.names[index]);
                legendHolders[legendHolderSelector].legends.index.push(index);
            }
            else if (!!defaultLegendHolder) {
                defaultLegendHolder.legends.labels.push(allLegends.labels[index]);
                defaultLegendHolder.legends.names.push(allLegends.names[index]);
                defaultLegendHolder.legends.index.push(index);
            }
        }
        dataOptions.legend.show = false;
        for (const legendHolderSelector of Object.keys(legendHolders)) {
            this.generateHandler(legendHolderSelector, cssTheme);
            const legendHolder = document.querySelector(legendHolderSelector);
            if (!legendHolder) {
                throw new Error(`Can't find legend holder using the selector ${legendHolderSelector}`);
            }
            document.querySelector(legendHolderSelector).innerHTML = this.generateLegend(legendHolderSelector, colors, legendHolders[legendHolderSelector].legends, cssTheme, mode, legendHolders[legendHolderSelector].orientation, dataOptions.legend && dataOptions.legend.formatter ? dataOptions.legend.formatter : undefined, legendHolders[legendHolderSelector].preItemContent, legendHolders[legendHolderSelector].postItemContent, legendHolders[legendHolderSelector].afterLegendContent);
        }
    }
    getLegendName(name, formatter) {
        let formatted = name;
        try {
            formatted = formatter ? formatter(name) : name;
        }
        catch (error) {
            console.error(`Error while formatting legend name ${name}: ${error}`);
        }
        return escapeHtml(formatted);
    }
    getSeriesMarker(cssTheme, markerColor) {
        var _a, _b;
        return `<span style="background-color:${markerColor}; ${ODSChartsItemCSSDefinition.getStyles((_a = cssTheme.legends) === null || _a === void 0 ? void 0 : _a.odsChartsLegendColor)}" class="ods-charts-legend-color ${ODSChartsItemCSSDefinition.getClasses((_b = cssTheme.legends) === null || _b === void 0 ? void 0 : _b.odsChartsLegendColor)}"></span>`;
    }
    generateLegend(legendHolderSelector, colors, legends, cssTheme, mode, orientation = 'horizontal', formatter, preItemContent, postItemContent, afterLegendContent) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        return `<div class="ods-charts-legend-holder ods-charts-mode-${mode} ${ODSChartsItemCSSDefinition.getClasses((_a = cssTheme.legends) === null || _a === void 0 ? void 0 : _a.odsChartsLegendHolder)}"
    style="${ODSChartsItemCSSDefinition.getStyles((_b = cssTheme.legends) === null || _b === void 0 ? void 0 : _b.odsChartsLegendHolder)}"
    >
    <div class="ods-charts-legend-container ods-charts-legend-container-${orientation} ${ODSChartsItemCSSDefinition.getClasses((_c = cssTheme.legends) === null || _c === void 0 ? void 0 : _c.odsChartsLegendContainer)} ${'vertical' === orientation ? ODSChartsItemCSSDefinition.getClasses((_d = cssTheme.legends) === null || _d === void 0 ? void 0 : _d.odsChartsLegendContainerVertical) : ODSChartsItemCSSDefinition.getClasses((_e = cssTheme.legends) === null || _e === void 0 ? void 0 : _e.odsChartsLegendContainerHorizontal)}"
    style="${ODSChartsItemCSSDefinition.getStyles((_f = cssTheme.legends) === null || _f === void 0 ? void 0 : _f.odsChartsLegendContainer)} ${'vertical' === orientation ? ODSChartsItemCSSDefinition.getStyles((_g = cssTheme.legends) === null || _g === void 0 ? void 0 : _g.odsChartsLegendContainerVertical) : ODSChartsItemCSSDefinition.getStyles((_h = cssTheme.legends) === null || _h === void 0 ? void 0 : _h.odsChartsLegendContainerHorizontal)}"
    >
    ${(legends ? legends.labels : []).map((legendLabel, indexInHolder) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
            let colorIndex = legends.index[indexInHolder] % colors.length;
            const postItemCustomContent = this.getCustomLegendItemContent(legendLabel, legends.names[indexInHolder], indexInHolder, colors[colorIndex], colorIndex, postItemContent);
            const preItemCustomContent = this.getCustomLegendItemContent(legendLabel, legends.names[indexInHolder], indexInHolder, colors[colorIndex], colorIndex, preItemContent);
            return `
      <span class="ods-charts-legend-item ${ODSChartsItemCSSDefinition.getClasses((_a = cssTheme.legends) === null || _a === void 0 ? void 0 : _a.odsChartsLegendItem)}"
        style="${ODSChartsItemCSSDefinition.getStyles((_b = cssTheme.legends) === null || _b === void 0 ? void 0 : _b.odsChartsLegendItem)}">
        <a class="ods-charts-legend-link ${ODSChartsItemCSSDefinition.getClasses((_c = cssTheme.legends) === null || _c === void 0 ? void 0 : _c.odsChartsLegendLink)}" 
          style="${ODSChartsItemCSSDefinition.getStyles((_d = cssTheme.legends) === null || _d === void 0 ? void 0 : _d.odsChartsLegendLink)}"
          href="javascript:" onclick="ods_chart_legend_switchLegend[${JSON.stringify(legendHolderSelector).replace(/"/g, '&quot;')}](this, ${JSON.stringify(legends.names[indexInHolder]).replace(/"/g, '&quot;')})">
          <span class="ods-charts-legend-color-holder ${ODSChartsItemCSSDefinition.getClasses((_e = cssTheme.legends) === null || _e === void 0 ? void 0 : _e.odsChartsLegendColorHolder)}"
          style="${ODSChartsItemCSSDefinition.getStyles((_f = cssTheme.legends) === null || _f === void 0 ? void 0 : _f.odsChartsLegendColorHolder)}">  
          ${this.getSeriesMarker(cssTheme, colors[colorIndex])}
          </span>
      ${preItemCustomContent
                ? `<span class="ods-charts-legend-pre-label-content" 
        ${ODSChartsItemCSSDefinition.getClasses((_g = cssTheme.legends) === null || _g === void 0 ? void 0 : _g.odsChartsLegendPreLabelContent)}
        style="${ODSChartsItemCSSDefinition.getStyles((_h = cssTheme.legends) === null || _h === void 0 ? void 0 : _h.odsChartsLegendPreLabelContent)}">${preItemCustomContent}</span>`
                : ''}
        <label class="ods-charts-legend-label ${ODSChartsItemCSSDefinition.getClasses((_j = cssTheme.legends) === null || _j === void 0 ? void 0 : _j.odsChartsLegendLabel)}"
        style="${ODSChartsItemCSSDefinition.getStyles((_k = cssTheme.legends) === null || _k === void 0 ? void 0 : _k.odsChartsLegendLabel)}"
        role="button">${this.getLegendName(legendLabel, formatter)}</label>
      </a>${postItemCustomContent
                ? `<span class="ods-charts-legend-custom-content" 
        ${ODSChartsItemCSSDefinition.getClasses((_l = cssTheme.legends) === null || _l === void 0 ? void 0 : _l.odsChartsLegendCustomContent)}
        style="${ODSChartsItemCSSDefinition.getStyles((_m = cssTheme.legends) === null || _m === void 0 ? void 0 : _m.odsChartsLegendCustomContent)}">${postItemCustomContent}</span>`
                : ''}
    </span>`;
        }).join(`
    `)}${afterLegendContent
            ? `<span
      class="ods-charts-legend-global-custom-content"
      ${ODSChartsItemCSSDefinition.getClasses((_j = cssTheme.legends) === null || _j === void 0 ? void 0 : _j.odsChartsLegendGlobalCustomContent)}
      style="${ODSChartsItemCSSDefinition.getStyles((_k = cssTheme.legends) === null || _k === void 0 ? void 0 : _k.odsChartsLegendGlobalCustomContent)}"
      >${afterLegendContent}</span>`
            : ''}
    </div>
    </div>`;
    }
    /**
     * Generates custom content for a legend item based on the provided postItemContent configuration.
     * @param legendLabel The displayed text of the legend item
     * @param legendName The name of the series associated with this legend item
     * @param legendIndex The index of this item within its legend group (0-based)
     * @param color The color assigned to this legend item, in CSS format (e.g. '#FF0000')
     * @param colorIndex The index of the color in the color palette (0-based)
     * @param itemCustomContent The configuration for custom content, can be either:
     *   - A function receiving all parameters and returning HTML content
     *   - An object mapping legend names to static HTML content
     * @returns The generated HTML content string to be displayed after the legend item
     */
    getCustomLegendItemContent(legendLabel, legendName, legendIndex, color, colorIndex, itemCustomContent) {
        if (!itemCustomContent) {
            return '';
        }
        if (typeof itemCustomContent === 'function') {
            return itemCustomContent(legendLabel, legendName, legendIndex, color, colorIndex);
        }
        if (typeof itemCustomContent === 'object' && !Array.isArray(itemCustomContent)) {
            return itemCustomContent[legendName] || itemCustomContent[legendLabel] || itemCustomContent[legendIndex] || '';
        }
        if (Array.isArray(itemCustomContent)) {
            return itemCustomContent[legendIndex] || '';
        }
        return '';
    }
    generateHandler(legendHolderSelector, cssTheme) {
        if (!window.ods_chart_legend_switchLegend) {
            window.ods_chart_legend_switchLegend = {};
        }
        window.ods_chart_legend_switchLegend[legendHolderSelector] = (elt, legendName) => {
            const visible = !elt.classList.contains('ods-charts-legend-link-opacity');
            const themeClasses = cssTheme.legends && cssTheme.legends.odsChartsLegendLinkOpacity && cssTheme.legends.odsChartsLegendLinkOpacity.classes
                ? cssTheme.legends.odsChartsLegendLinkOpacity.classes
                : [];
            const themeStyles = visible
                ? cssTheme.legends && cssTheme.legends.odsChartsLegendLinkOpacity && cssTheme.legends.odsChartsLegendLinkOpacity.styles
                    ? cssTheme.legends.odsChartsLegendLinkOpacity.styles
                    : {}
                : cssTheme.legends && cssTheme.legends.odsChartsLegendLink && cssTheme.legends.odsChartsLegendLink.styles
                    ? cssTheme.legends.odsChartsLegendLink.styles
                    : {};
            if (visible) {
                elt.classList.add('ods-charts-legend-link-opacity');
                for (const themeClass of themeClasses) {
                    elt.classList.add(themeClass);
                }
            }
            else {
                elt.classList.remove('ods-charts-legend-link-opacity');
                for (const themeClass of themeClasses) {
                    elt.classList.remove(themeClass);
                }
            }
            for (const cssProperty of Object.keys(themeStyles)) {
                elt.style[cssProperty] = themeStyles[cssProperty];
            }
            this.echart.dispatchAction({
                type: visible ? 'legendUnSelect' : 'legendSelect',
                name: legendName,
            });
        };
    }
}
